import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';
import { FaBuilding, FaHardHat, FaCity, FaHome } from 'react-icons/fa';

const BuildingConstructionPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Industries', path: '/industries' },
    { label: 'Building & Construction' }
  ];

  const services = [
    {
      icon: <FaBuilding className="w-8 h-8 text-secondary" />,
      title: "Real Estate Development",
      description: "Strategic financial planning and capital structuring for residential, commercial, and mixed-use development projects."
    },
    {
      icon: <FaHardHat className="w-8 h-8 text-secondary" />,
      title: "Construction Financing",
      description: "Specialized funding solutions for construction projects, including acquisition loans, construction loans, and bridge financing."
    },
    {
      icon: <FaCity className="w-8 h-8 text-secondary" />,
      title: "Infrastructure Investment",
      description: "Advisory services for public and private infrastructure projects, focusing on sustainable and high-impact developments."
    },
    {
      icon: <FaHome className="w-8 h-8 text-secondary" />,
      title: "Property Investment",
      description: "Comprehensive investment strategies for residential and commercial property portfolios to maximize returns and mitigate risks."
    }
  ];

  const clientTestimonials = [
    {
      quote: "Segway Capital's expertise in real estate financing has been crucial to our development projects. Their innovative solutions helped us overcome complex financial challenges.",
      author: "Thomas Anderson",
      position: "Director, Skyline Developers"
    },
    {
      quote: "The infrastructure investment advice we received from Segway Capital enabled us to secure funding for our sustainable urban development initiative.",
      author: "Rebecca Martinez",
      position: "CEO, Urban Future Group"
    },
    {
      quote: "With Segway Capital's guidance, we've transformed our property investment strategy and achieved significantly higher returns while reducing exposure to market volatility.",
      author: "David Wilson",
      position: "Principal, Wilson Properties"
    }
  ];

  return (
    <>
      <PageHeader
        title="Building & Construction"
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/house-investments-elements-assortment_23-2148793805.jpg_ml0e0k.jpg"
      />

      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
                Financial Solutions for Building & Construction
              </h2>
              <p className="text-lg text-gray-700">
                Segway Capital provides specialized financial services for the building and construction industry, helping clients navigate the unique challenges of real estate development, construction financing, and property investment.
              </p>
              <p className="text-lg text-gray-700">
                Our team combines deep industry knowledge with financial expertise to deliver solutions that support sustainable growth, manage risk, and maximize returns on construction and real estate projects.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact-us" 
                  className="px-8 py-3 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors inline-block"
                >
                  Discuss Your Project
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/couple-accepting-keys-their-new-house_23-2148346270.jpg_jurphw.jpg" 
                alt="Building and Construction" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Our Services for the Building & Construction Industry
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We offer tailored financial solutions to address the specific needs of construction companies, real estate developers, and property investors.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {services.map((service, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{service.icon}</div>
                <h4 className="text-xl font-heading font-semibold mb-3">{service.title}</h4>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Client Success Stories
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              See how we've helped our clients achieve success in the building and construction industry.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {clientTestimonials.map((testimonial, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md"
              >
                <div className="text-4xl text-primary opacity-30 mb-4">"</div>
                <p className="text-gray-700 italic mb-6">{testimonial.quote}</p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-primary">{testimonial.author}</p>
                  <p className="text-sm text-gray-500">{testimonial.position}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="bg-primary text-white rounded-xl p-10 shadow-lg">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl md:text-3xl font-heading font-bold mb-4">Ready to Build Your Financial Strategy?</h3>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                Our construction and real estate financial experts are ready to help you achieve your development and investment goals.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Link 
                to="/contact-us" 
                className="px-8 py-3 bg-white text-primary hover:bg-gray-100 font-semibold rounded-lg transition-colors inline-block"
              >
                Schedule a Consultation
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BuildingConstructionPage; 